import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  TablePagination,
  InputBase,
  Avatar,
  Stack,
  Grid,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import {
  Search,
  Plus,
  Trash2,
  Edit,
  ExternalLink,
  Newspaper,
  Star,
  CheckCircle2,
  Radio,
  Tv,
  Megaphone,
} from 'lucide-react';
import axiosInstance, { API_URL } from '../utils/axios';
import { getNewsImageUrl } from '../utils/helpers';
import NewsPrModal from '../components/NewsPrModal';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import { TableLoadingSkeleton } from '../components/common/LoadingState';
import ExportTableButton from '../components/common/ExportTableButton';

const TYPE_CONFIG = {
  press_release: { label: 'Press Release', color: 'primary', icon: <Megaphone size={13} />, bg: '#e0f2fe', text: '#0369a1' },
  media_coverage: { label: 'Media Coverage', color: 'secondary', icon: <Tv size={13} />, bg: '#f3e8ff', text: '#7e22ce' },
  news: { label: 'Company News', color: 'info', icon: <Radio size={13} />, bg: '#ecfdf5', text: '#047857' },
};

const NewsPrView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Data states
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    pressReleases: 0,
    mediaCoverage: 0,
  });

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'press_release' | 'media_coverage' | 'news' | 'draft'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Delete modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch articles
  const fetchNewsPr = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm.trim() || undefined,
      };

      if (activeTab === 'draft') {
        params.status = 'draft';
      } else if (activeTab !== 'all') {
        params.type = activeTab;
        params.status = 'published';
      }

      const res = await axiosInstance.get('/admin/news', { params });
      if (res.data?.success) {
        setItems(res.data.data || []);
        setTotalCount(res.data.total || 0);
        setStats(res.data.stats || {});
      }
    } catch (err) {
      console.error('Failed to fetch News & PR items:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, activeTab]);

  useEffect(() => {
    fetchNewsPr();
  }, [fetchNewsPr]);

  // Handle open create modal
  const handleOpenCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  // Handle open edit modal
  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  // Quick toggle status (published vs draft)
  const handleToggleStatus = async (item) => {
    const nextStatus = item.status === 'published' ? 'draft' : 'published';
    try {
      await axiosInstance.put(`/admin/news/${item._id}`, { status: nextStatus });
      fetchNewsPr();
    } catch (err) {
      console.error('Failed to toggle status:', err);
    }
  };

  // Quick toggle featured flag
  const handleToggleFeatured = async (item) => {
    try {
      await axiosInstance.put(`/admin/news/${item._id}`, { is_featured: !item.is_featured });
      fetchNewsPr();
    } catch (err) {
      console.error('Failed to toggle featured:', err);
    }
  };

  // Open delete confirm dialog
  const handleOpenDelete = (item) => {
    setDeletingItem(item);
    setDeleteModalOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!deletingItem || isDeleting) return;
    setIsDeleting(true);
    try {
      await axiosInstance.delete(`/admin/news/${deletingItem._id}`);
      setDeleteModalOpen(false);
      fetchNewsPr();
    } catch (err) {
      console.error('Failed to delete item:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Top Header */}
      <PageHeader
        title="Newsroom & Press Releases (PR)"
        subtitle="Publish official company news, announcements, press releases, and media coverage features."
        count={totalCount}
        countLabel="articles"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Content', href: '/news-pr' },
          { label: 'News & PR' }
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={handleOpenCreate}
            sx={{
              fontWeight: 700,
              borderRadius: 2,
              px: 2.5,
              py: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              boxShadow: '0 4px 12px rgba(247, 93, 52, 0.25)',
            }}
          >
            Add News / PR
          </Button>
        }
        secondaryAction={
          <ExportTableButton
            data={items}
            total={totalCount}
            onExportAll={async () => {
              const params = {
                all: true,
                search: searchTerm.trim() || undefined,
              };
              if (activeTab === 'draft') {
                params.status = 'draft';
              } else if (activeTab !== 'all') {
                params.type = activeTab;
                params.status = 'published';
              }
              const res = await axiosInstance.get('/admin/news', { params });
              return res.data?.data || [];
            }}
            columns={[
              { label: 'Title', key: 'title' },
              { label: 'Type', value: (r) => TYPE_CONFIG[r.type]?.label || r.type },
              { label: 'Media Outlet', key: 'media_outlet' },
              { label: 'Published Date', value: (r) => r.published_date ? new Date(r.published_date).toLocaleDateString() : 'N/A' },
              { label: 'Status', key: 'status' },
              { label: 'Featured', value: (r) => r.is_featured ? 'Yes' : 'No' },
            ]}
            filename="news_pr"
          />
        }
      />

      {/* Metric Summary Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Total Articles
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', mt: 0.5 }}>
                  {stats.total || 0}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  color: 'primary.main',
                  width: 48,
                  height: 48,
                }}
              >
                <Newspaper size={24} />
              </Avatar>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Published Live
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.dark', mt: 0.5 }}>
                  {stats.published || 0}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.15),
                  color: 'success.dark',
                  width: 48,
                  height: 48,
                }}
              >
                <CheckCircle2 size={24} />
              </Avatar>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Press Releases
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'info.main', mt: 0.5 }}>
                  {stats.pressReleases || 0}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.15),
                  color: 'info.main',
                  width: 48,
                  height: 48,
                }}
              >
                <Megaphone size={24} />
              </Avatar>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.secondary.main, 0.08),
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Media Coverage
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'secondary.main', mt: 0.5 }}>
                  {stats.mediaCoverage || 0}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.secondary.main, 0.15),
                  color: 'secondary.main',
                  width: 48,
                  height: 48,
                }}
              >
                <Tv size={24} />
              </Avatar>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table Card */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        {/* Filter and Search Bar */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, val) => {
              setActiveTab(val);
              setPage(0);
            }}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                minHeight: 40,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.88rem',
              },
            }}
          >
            <Tab label={`All (${stats.total || 0})`} value="all" />
            <Tab label={`Press Releases (${stats.pressReleases || 0})`} value="press_release" />
            <Tab label={`Media Coverage (${stats.mediaCoverage || 0})`} value="media_coverage" />
            <Tab label="Company News" value="news" />
            <Tab
              label={
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <span>Drafts</span>
                  {stats.draft > 0 && (
                    <Chip label={stats.draft} size="small" sx={{ height: 18, fontSize: '0.75rem', fontWeight: 700 }} />
                  )}
                </Stack>
              }
              value="draft"
            />
          </Tabs>

          {/* Search Box */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#f5f7fa',
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              width: { xs: '100%', sm: 280 },
              border: '1px solid #e0e0e0',
            }}
          >
            <Search size={18} color="#78909c" />
            <InputBase
              placeholder="Search headline, source, tags..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              sx={{ ml: 1, flex: 1, fontSize: '0.88rem' }}
            />
          </Box>
        </Box>

        {/* Articles Table */}
        <TableContainer>
          <Table sx={{ minWidth: 850 }}>
            <TableHead sx={{ bgcolor: '#fafafa' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: 95 }}>Cover</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Headline &amp; Summary</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Media Outlet / Source</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingSkeleton rows={rowsPerPage} columns={7} />
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ p: 0 }}>
                    <EmptyState
                      icon={Newspaper}
                      title="No news or PR articles found"
                      description={searchTerm ? 'Try a different search keyword.' : 'Publish official company announcements, press releases, and media features.'}
                      actionLabel="Add News / PR"
                      onAction={handleOpenCreate}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => {
                  const typeCfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.press_release;
                  const isPublished = item.status === 'published';

                  return (
                    <TableRow key={item._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      {/* Cover Thumbnail */}
                      <TableCell sx={{ width: 95, py: 1.5 }}>
                        <Avatar
                          variant="rounded"
                          src={getNewsImageUrl(item.cover_image) || undefined}
                          imgProps={{
                            referrerPolicy: 'no-referrer',
                            onError: (e) => {
                              if (item.cover_image && !e.target.src.includes('/proxy-image')) {
                                e.target.src = `${API_URL}/admin/news/proxy-image?url=${encodeURIComponent(item.cover_image)}`;
                              }
                            },
                          }}
                          sx={{
                            width: 76,
                            height: 48,
                            borderRadius: 2,
                            border: '1px solid #e2e8f0',
                            bgcolor: '#ffffff',
                            color: '#94a3b8',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            p: 0.5,
                            '& .MuiAvatar-img': {
                              objectFit: 'contain',
                              width: '100%',
                              height: '100%',
                            },
                          }}
                        >
                          <Newspaper size={20} />
                        </Avatar>
                      </TableCell>

                      {/* Title & Summary */}
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.5 }}>
                          {item.is_featured && (
                            <Tooltip title="Featured on top of newsroom">
                              <Star size={16} color="#eab308" fill="#eab308" />
                            </Tooltip>
                          )}
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
                            {item.title}
                          </Typography>
                        </Stack>

                        {item.summary && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.4,
                              mb: 0.5,
                            }}
                          >
                            {item.summary}
                          </Typography>
                        )}

                        {/* Tags */}
                        {item.tags && item.tags.length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                            {item.tags.slice(0, 3).map((tag, idx) => (
                              <Chip
                                key={idx}
                                label={tag}
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#f1f5f9', color: '#475569' }}
                              />
                            ))}
                          </Stack>
                        )}
                      </TableCell>

                      {/* Type Badge */}
                      <TableCell>
                        <Chip
                          icon={typeCfg.icon}
                          label={typeCfg.label}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            bgcolor: typeCfg.bg,
                            color: typeCfg.text,
                          }}
                        />
                      </TableCell>

                      {/* Source */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                          {item.media_source || 'Press Release'}
                        </Typography>
                        {item.external_url && (
                          <Typography
                            component="a"
                            href={item.external_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            sx={{
                              color: 'primary.main',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.3,
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            View publication <ExternalLink size={11} />
                          </Typography>
                        )}
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Tooltip title="Click to toggle publish status">
                          <Chip
                            label={isPublished ? 'Published' : 'Draft'}
                            color={isPublished ? 'success' : 'default'}
                            size="small"
                            onClick={() => handleToggleStatus(item)}
                            sx={{ fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </TableCell>

                      {/* Published Date */}
                      <TableCell>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {item.published_date
                            ? new Date(item.published_date).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })
                            : '-'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                          By: {item.author || 'Desk'}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.8} justifyContent="flex-end">
                          <Tooltip title={item.is_featured ? 'Featured on homepage' : 'Mark as featured'}>
                            <IconButton size="small" onClick={() => handleToggleFeatured(item)} sx={{ color: item.is_featured ? '#eab308' : 'text.disabled' }}>
                              <Star size={16} fill={item.is_featured ? '#eab308' : 'none'} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Edit Article">
                            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(item)}>
                              <Edit size={16} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Article">
                            <IconButton size="small" color="error" onClick={() => handleOpenDelete(item)}>
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 1.5 } }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[6, 12, 24, 48]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      </Card>

      {/* Create / Edit Modal */}
      <NewsPrModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchNewsPr}
        editData={editingItem}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => !isDeleting && setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Article"
        content={`Are you sure you want to delete the article "${deletingItem?.title || ''}"? This action cannot be undone.`}
        confirmText="Delete Article"
        isLoading={isDeleting}
        severity="error"
      />
    </Box>
  );
};

export default NewsPrView;
